import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react';

import { useSounds } from '../../hooks/useSounds';
import { useFlag } from '../../services/launchDarkly';
import {
  isChallengeBundleItem,
  isFreeSpinsBundleItem,
  isMetaGameBundleItem,
  isMetaGameFeatureBundleItem,
  isSnappyGiftBundleItem,
} from '../../stores/rewards/helpers';
import type { MetaGameTokenPayload } from '../../stores/rewards/types';
import { useStores } from '../../stores/useStores';
import ChallengeRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/ChallengeRewardComponent/ChallengeRewardComponent';
import MetaGameRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/MetaGameRewardComponent/MetaGameRewardComponent';
import { FRBWidget } from '../FRBWidget/FRBWidget';
import { SnappyGiftReward } from '../SnappyGiftReward/SnappyGiftReward';
import { SocialCoin } from '../SocialCoin/SocialCoin';
import { SweepCoin } from '../SweepCoin/SweepCoin';

import {
  containerVariants,
  FADE_UP_STAGGER_DELAY,
  INITIAL_ANIMATION_DURATION,
  itemVariants,
  SOUND_DELAY,
  STAGGER_DELAY,
  WAIT_TIME,
} from './animations';
import classes from './ClaimRewardsAnimation.module.scss';
import { CLAIM_REWARDS_ANIMATION_SOUNDS } from './sounds';

type RewardType = 'SC' | 'GC' | 'freeSpins' | 'metagame' | 'metagameFeature' | 'snappyGift' | 'challenge';
const REWARDS_ORDER: RewardType[] = ['GC', 'SC', 'freeSpins', 'metagame', 'metagameFeature', 'snappyGift', 'challenge'];

function RewardMotionWrapper({
  children,
  className,
  key,
}: {
  children: React.ReactNode;
  className: string;
  key: string;
}) {
  return (
    <motion.div key={key} className={classNames(classes.rewardItem, className)} variants={itemVariants}>
      {children}
    </motion.div>
  );
}

function ClaimRewardsAnimationComponent() {
  {
    const { playSound } = useSounds(CLAIM_REWARDS_ANIMATION_SOUNDS);

    const {
      uiStore,
      walletStore,
      userFreeSpinsStore,
      metagamesStore,
      animationStore,
      userSnappyGiftsStore,
      challengesStore,
      featureManagerStore,
    } = useStores();
    const { claimRewardAnimationState } = uiStore;
    const [shouldFadeUp, setShouldFadeUp] = useState(false);
    const isNewRewardsAnimationEnabled = useFlag('new_rewards_animation', false);

    // Calculate fontSize based on fixed container width (120px)
    // metagame: 19% of 120px = 22.8px, freeSpin: 10% of 120px = 12px
    const REWARD_ITEM_WIDTH = 120;
    const metagameFontSize = (19 / 100) * REWARD_ITEM_WIDTH; // ~22.8px
    const freeSpinFontSize = (8 / 100) * REWARD_ITEM_WIDTH; // 12px

    const { enable, rewards, refetchData, enableSounds, onEnd, hideOverlay } = claimRewardAnimationState;

    const bundleItems =
      rewards
        ?.flatMap((pkg) => pkg.bundleItems)
        .filter((item, index, self) => index === self.findIndex((i) => i.id === item.id)) || [];

    const freeSpinPayloads = bundleItems?.filter(isFreeSpinsBundleItem).map((item) => item.bundlePayload);
    const snappyGiftPayloads = bundleItems?.filter(isSnappyGiftBundleItem).map((item) => item.bundlePayload);
    const challengeRewardPayloads = bundleItems?.filter(isChallengeBundleItem).map((item) => item.bundlePayload);
    const metagameFeaturePayloads = bundleItems?.filter(isMetaGameFeatureBundleItem).map((item) => item.bundlePayload);

    // Merge metagame rewards by metagameTypeId and coinType, then sum amounts
    const metagameItems = bundleItems?.filter(isMetaGameBundleItem) || [];
    const metagamesRewardsPayloads = metagameItems.reduce((acc, item) => {
      const payload = item.bundlePayload;
      const existing = acc.find((p) => p.metagameTypeId === payload.metagameTypeId && p.coinType === payload.coinType);

      if (existing) {
        existing.amount += payload.amount;
      } else {
        acc.push({ ...payload });
      }

      return acc;
    }, [] as MetaGameTokenPayload[]);

    const gcAmount = rewards?.reduce((acc, pkg) => acc + pkg.gcCoin, 0) || 0;
    const scAmount = rewards?.reduce((acc, pkg) => acc + pkg.scCoin, 0) || 0;

    // Helper to create coin reward element
    const createCoinReward = (type: RewardType, amount: number) => ({
      type,
      element: (
        <RewardMotionWrapper key={type} className={classNames(classes.rewardItem, classes[type])}>
          {type === 'SC' ? (
            <div className={classes.coinRewardWrapper}>
              <SweepCoin isPile />
              {amount.toLocaleString()}
            </div>
          ) : (
            <div className={classes.coinRewardWrapper}>
              <SocialCoin isPile />
              {amount.toLocaleString()}
            </div>
          )}
        </RewardMotionWrapper>
      ),
    });

    // Collect and sort rewards according to REWARDS_ORDER
    const rewardElements: { type: RewardType; element: React.ReactNode }[] = [
      ...(gcAmount ? [createCoinReward('GC', gcAmount)] : []),
      ...(scAmount ? [createCoinReward('SC', scAmount)] : []),
      ...snappyGiftPayloads.map((payload, index) => ({
        type: 'snappyGift' as RewardType,
        element: (
          <RewardMotionWrapper key={`snappy-gift-${index}`} className={classes.snappyGift}>
            <SnappyGiftReward snappyGiftPayload={payload} />
          </RewardMotionWrapper>
        ),
      })),
      ...metagamesRewardsPayloads.map((payload, index) => ({
        type: 'metagame' as RewardType,
        element: (
          <RewardMotionWrapper key={`metagame-${index}`} className={classes.metagame}>
            <MetaGameRewardComponent
              amount={payload.amount}
              className={classes.metagameReward}
              currencySymbol={payload.currencySymbol}
              fontSize={metagameFontSize}
            />
          </RewardMotionWrapper>
        ),
      })),
      ...metagameFeaturePayloads.map((payload, index) => ({
        type: 'metagameFeature' as RewardType,
        element: (
          <RewardMotionWrapper key={`metagameFeature-${index}`} className={classes.metagame}>
            <div className={classes.metagameFeatureReward}>
              <img src={payload.symbolUrl} />
              <span>{payload.amount}</span>
            </div>
          </RewardMotionWrapper>
        ),
      })),
      ...freeSpinPayloads.map((payload, index) => ({
        type: 'freeSpins' as RewardType,
        element: (
          <RewardMotionWrapper key={`freeSpins-${index}`} className={classes.freeSpin}>
            <FRBWidget
              betAmount={payload.bet || 10}
              coinType={payload.coinType}
              displayCount={payload.displayCount}
              fontSize={freeSpinFontSize}
              gameImage={payload.gameImageUrl}
              provider={payload.provider}
              rounds={payload.rounds || 0}
              type={payload.type as 'freeSpins'}
              usePropFontSize={true}
              width={'80%'}
            />
          </RewardMotionWrapper>
        ),
      })),
      ...challengeRewardPayloads.map((payload, index) => ({
        type: 'challenge' as RewardType,
        element: (
          <RewardMotionWrapper key={`challenge-${index}`} className={classes.challenge}>
            <ChallengeRewardComponent className={classes.challengeReward} symbolUrl={payload.symbolUrl} />
          </RewardMotionWrapper>
        ),
      })),
    ].sort((a, b) => {
      const indexA = REWARDS_ORDER.indexOf(a.type);
      const indexB = REWARDS_ORDER.indexOf(b.type);
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    const totalItems = rewardElements.length;

    useEffect(() => {
      if (!claimRewardAnimationState.enable) {
        setShouldFadeUp(false);
        return;
      }

      const totalAnimationTime = (totalItems - 1) * STAGGER_DELAY + INITIAL_ANIMATION_DURATION + WAIT_TIME;
      const timer = setTimeout(() => {
        setShouldFadeUp(true);
      }, totalAnimationTime * 1000);

      return () => clearTimeout(timer);
    }, [claimRewardAnimationState.enable, totalItems]);

    const animationCompleteRef = useRef(false);

    const handleAnimationComplete = useCallback(() => {
      if (animationCompleteRef.current) {
        return;
      }
      animationCompleteRef.current = true;
      void onEnd?.();
    }, [onEnd]);

    // Handle animation complete - call onEnd when fadeUp animation finishes
    useEffect(() => {
      if (!shouldFadeUp || !onEnd) {
        animationCompleteRef.current = false;
        return;
      }

      // Calculate when the last item finishes fading out
      const fadeUpDuration = 0.5; // Duration from itemVariants fadeUp transition
      const fadeUpStaggerTime = totalItems > 0 ? (totalItems - 1) * FADE_UP_STAGGER_DELAY : 0;
      const totalFadeUpTime = (fadeUpDuration + fadeUpStaggerTime) * 1000;

      const timer = setTimeout(() => {
        animationStore.setAnimationBlockScreenVisible(false);
        handleAnimationComplete();
      }, totalFadeUpTime);

      return () => {
        clearTimeout(timer);
        animationCompleteRef.current = false;
      };
    }, [shouldFadeUp, totalItems, handleAnimationComplete, onEnd, animationStore]);

    useEffect(() => {
      if (refetchData) {
        void walletStore.fetchWallet();
        const hasMiniGames = freeSpinPayloads.some((item) =>
          ['Barouletta Gaming', 'Minigames'].includes(item.provider),
        );

        if (freeSpinPayloads.length > 0 && !hasMiniGames) {
          void userFreeSpinsStore.fetch();
        }
        if (metagamesRewardsPayloads.length > 0) {
          void metagamesStore.fetch();
        }
        if (snappyGiftPayloads.length > 0) {
          void userSnappyGiftsStore.fetch();
        }

        if (challengeRewardPayloads.length > 0) {
          void (async () => {
            const fetchResult = await challengesStore.fetch();
            if (fetchResult === 'READY' && featureManagerStore.getFeatureStatus('Challenges') === 'FETCHING') {
              featureManagerStore.updateFeatureStatus('Challenges', 'READY');
              featureManagerStore.addDialogsToSequenceQueue(['Challenges']);
            }
          })();
        }
      }
    }, [
      refetchData,
      freeSpinPayloads,
      metagamesRewardsPayloads,
      walletStore,
      userFreeSpinsStore,
      metagamesStore,
      userSnappyGiftsStore,
      snappyGiftPayloads,
      challengeRewardPayloads,
      challengesStore,
      featureManagerStore,
    ]);

    useEffect(() => {
      if (enableSounds && enable) {
        playSound('rewards', {
          singleton: true,
          loop: false,
          delay: SOUND_DELAY,
        });
      }
    }, [enableSounds, playSound, enable]);

    if (!isNewRewardsAnimationEnabled) return null;

    return (
      <>
        {enable && (
          <div
            className={classNames(classes.claimRewardsAnimation, hideOverlay && classes.claimRewardsAnimationNoOverlay)}
          >
            <motion.div
              animate={shouldFadeUp ? 'fadeUp' : 'visible'}
              className={classes.rewardItems}
              initial="hidden"
              variants={containerVariants}
            >
              {rewardElements.map((reward) => reward.element)}
            </motion.div>
          </div>
        )}
      </>
    );
  }
}

export const ClaimRewardsAnimation = observer(ClaimRewardsAnimationComponent);
